import { useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { QueryErrorState } from '../../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../../components/dashboard/QueryLoadingBlock';
import { useDesktopLayout } from '../../../layout/useDesktopLayout';
import { api } from '../../../lib/api';
import { getOperatorDisplayStatus } from '../../../lib/dashboard/operatorDisplayStatus';
import { getCardConnectionStatus } from '../../../lib/dashboard/screenStatus';
import { useQueryRetry } from '../../../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../../../lib/session';
import { ScreenManagePanel } from '../ScreenManagePanel';
import { DisplayManageMobileChrome } from './DisplayManageMobileChrome';
import { displayManageSearchPath } from './displayManageRoutes';

/**
 * Dedicated manage route for the mobile companion.
 * At `lg+`, hands off to the desktop sheet via `/screens?manage=`.
 */
export function DisplayManagePage() {
  const { t } = useTranslation('displays');
  const { screenId = '' } = useParams<{ screenId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useDesktopLayout();
  const organisationId = useOrganisationId();
  const startPlayContent = searchParams.get('play') === '1';

  useEffect(() => {
    if (!isDesktop || !screenId) return;
    navigate(
      displayManageSearchPath(screenId, { playContent: startPlayContent }),
      { replace: true },
    );
  }, [isDesktop, navigate, screenId, startPlayContent]);

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 15_000,
    enabled: !isDesktop && Boolean(screenId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
    enabled: !isDesktop && Boolean(screenId),
  });

  const { isRetrying, retry } = useQueryRetry(async () => {
    await Promise.all([screensQuery.refetch(), playlistsQuery.refetch()]);
  });

  if (isDesktop) {
    return <QueryLoadingBlock />;
  }

  if (!screenId) {
    return (
      <QueryEmptyNavigate />
    );
  }

  if (screensQuery.isError || playlistsQuery.isError || isRetrying) {
    return (
      <QueryErrorState
        message={t('manage.mobile.loadError')}
        onRetry={retry}
        isRetrying={isRetrying}
      />
    );
  }

  if (screensQuery.isPending || playlistsQuery.isPending) {
    return <QueryLoadingBlock variant="cards" />;
  }

  const screen = screensQuery.data?.find((item) => item.id === screenId);
  if (!screen) {
    return <QueryEmptyNavigate />;
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5 lg:hidden">
      <DisplayManageMobileChrome
        screenName={screen.name}
        status={getOperatorDisplayStatus(screen)}
      />
      <ScreenManagePanel
        organisationId={organisationId}
        screen={screen}
        playlists={playlistsQuery.data ?? []}
        unpaired={getCardConnectionStatus(screen) === 'unpaired'}
        startPlayContent={startPlayContent}
        onDeleted={() => navigate('/screens', { replace: true })}
      />
    </div>
  );
}

function QueryEmptyNavigate() {
  const { t } = useTranslation('displays');
  return (
    <div
      className="rounded-[14px] border px-4 py-5 text-sm"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)', color: 'var(--gs-muted)' }}
    >
      <Trans
        t={t}
        i18nKey="manage.mobile.notFound"
        components={{
          screensLink: <Link to="/screens" className="font-semibold" style={{ color: 'var(--gs-accent)' }} />,
        }}
      />
    </div>
  );
}
